import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { getZendeskHelpCentreUrl } from '@skyscanner/zendesk-url';
import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import Heart from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/heart';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text/src/BpkText';
import LoginForm from '@web-platform/global-components-login-form';
import observer from 'saddlebag-observer';

import CultureSelectorButton from '../CultureSelectorButton';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import LoginButton from '../LoginButton';
import { LOGIN_EVENTS } from '../constants';
import { behaviourEvents, SAVED_BUTTON_COMPONENT_NAME } from '../userTrackingEvents';
import handleClickAndTrack from '../utils/handleClickAndTrack';

import type {
  CultureSelectorEmbeddedProps,
  RedirectFunc,
  Strings,
  LoginFormEmbeddedProps,
} from '../types';
import type { OnCultureSelectorButtonClick } from '@web-platform/global-components-culture-selector/helpers/useCultureSelectorData';
import type { LoginOnClose } from '@web-platform/global-components-login-form/types';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './SecondaryNav.module.scss';

const HeartIcon = withRtlSupport(Heart);

type Props = {
  activeTab: string;
  cultureSelectorProps?: CultureSelectorEmbeddedProps;
  currency: string;
  currencyLabel: string;
  getProfilePicture: () => string | null;
  locale: string;
  localeName: string;
  logger: Logger;
  market: string;
  marketName: string;
  redirect: RedirectFunc;
  showCultureSelectorButton: boolean;
  strings: Strings;
  userLoggedIn: Promise<boolean> | Promise<undefined>;
  trackEventFn: TrackingEventFn;
  loginFormProps?: LoginFormEmbeddedProps;
  onCultureSelectorButtonClick: OnCultureSelectorButtonClick;
  isFetchingCultureSelectorData?: boolean;
  loginButtonVariant?: string;
};

const { LOGIN_BUTTON_CLICKED } = LOGIN_EVENTS;
const PROFILE_PATH = '/profile';

const getSavedButtonBehaviourEventOptions = (
  isLoggedIn?: boolean,
): { booleanProperties: Record<string, boolean> } => ({
  booleanProperties: isLoggedIn === undefined ? {} : { is_logged_in: isLoggedIn },
});

type SavedListButtonProps = {
  ariaLabel: string;
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
};

const SavedListButton = ({ ariaLabel, onClick }: SavedListButtonProps) => (
  <BpkButton
    type={BUTTON_TYPES.linkOnDark}
    href="/profile/saved"
    className={STYLES.SecondaryNav__heartIcon}
    onClick={onClick}
    aria-label={ariaLabel}
    iconOnly
  >
    <HeartIcon />
  </BpkButton>
);

const SecondaryNav = ({
  activeTab,
  cultureSelectorProps,
  currency,
  currencyLabel,
  getProfilePicture,
  isFetchingCultureSelectorData,
  locale,
  localeName,
  logger,
  loginButtonVariant,
  loginFormProps,
  market,
  marketName,
  onCultureSelectorButtonClick,
  redirect,
  showCultureSelectorButton,
  strings,
  trackEventFn,

  userLoggedIn,
}: Props) => {
  const eventTracker = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    id: 'authenticationlink' | 'savedList' | 'ssHeaderLinksFaq',
    eventType: string,
  ) => {
    handleClickAndTrack(e, id, eventType, logger, trackEventFn);
  };

  const [isLoggedInState, setIsLoggedInState] = useState<boolean>();
  const hasTrackedSavedButtonImpression = useRef(false);

  const ensureSavedButtonImpression = useCallback(
    (isLoggedIn?: boolean) => {
      if (hasTrackedSavedButtonImpression.current) return;

      hasTrackedSavedButtonImpression.current = true;
      const impressionEvent = behaviourEvents.getImpressionEvent(
        undefined,
        SAVED_BUTTON_COMPONENT_NAME,
        getSavedButtonBehaviourEventOptions(isLoggedIn),
      );
      if (impressionEvent) trackEventFn([impressionEvent]);
    },
    [trackEventFn],
  );

  useEffect(() => {
    let isMounted = true;

    const trackSavedButtonImpression = (isLoggedIn?: boolean) => {
      if (!isMounted) return;

      setIsLoggedInState(isLoggedIn);
      ensureSavedButtonImpression(isLoggedIn);
    };

    userLoggedIn.then(trackSavedButtonImpression, () => trackSavedButtonImpression());

    return () => {
      isMounted = false;
    };
  }, [ensureSavedButtonImpression, userLoggedIn]);

  useEffect(() => {
    const loginSuccessSubscribeHandle = observer.subscribe('sid-login-success', () => {
      setIsLoggedInState(true);
    });

    return () => {
      observer.unsubscribe(loginSuccessSubscribeHandle);
    };
  }, []);

  const onSavedListClick = (e: MouseEvent<HTMLAnchorElement>) => {
    eventTracker(e, 'savedList', 'Clicked');
    const eventOptions = getSavedButtonBehaviourEventOptions(isLoggedInState);

    ensureSavedButtonImpression(isLoggedInState);

    const clickEvent = behaviourEvents.getClickEvent(
      undefined,
      SAVED_BUTTON_COMPONENT_NAME,
      eventOptions,
    );
    if (clickEvent) trackEventFn([clickEvent]);
  };

  const onSavedButtonError = ({ component, error }: { component: string; error: Error }) => {
    logger.logError(error, {
      component,
      message: 'Error caught by error boundary',
      stack: error.stack,
    });
  };

  const loginClickHandler = (
    e: MouseEvent<HTMLButtonElement>,
    isLoggedIn: boolean,
    authConsumer: string,
  ) => {
    eventTracker(e, 'authenticationlink', 'Clicked');
    logger.logOperationalEvent({
      eventName: LOGIN_BUTTON_CLICKED,
      authConsumer,
      market,
    });
    if (isLoggedIn) {
      redirect(PROFILE_PATH);
      return;
    }

    if (loginFormProps) {
      loginFormProps.onVisibilityChange({ shouldBeOpen: true });
    }
  };

  const onLoginFormClose = ({ isLoggedIn }: LoginOnClose) => {
    loginFormProps?.onVisibilityChange({
      loginFlowComplete: isLoggedIn,
      shouldBeOpen: false,
    });
  };

  const cultureSelectorButton = (
    <CultureSelectorButton
      locale={locale}
      localeName={localeName}
      marketName={marketName}
      market={market}
      currencyLabel={currencyLabel}
      currency={currency}
      cultureSelectorProps={cultureSelectorProps}
      logger={logger}
      trackEventFn={trackEventFn}
      onCultureSelectorButtonClick={onCultureSelectorButtonClick}
      isFetching={isFetchingCultureSelectorData}
    />
  );

  return (
    <>
      <nav className={STYLES.SecondaryNav} aria-label={strings.userPreferences}>
        <ul className={STYLES.SecondaryNav__list}>
          <li className={STYLES.SecondaryNav__help}>
            <BpkLink
              id="ss-header-links-faq"
              href={getZendeskHelpCentreUrl(activeTab, locale, market, currency)}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                eventTracker(e, 'ssHeaderLinksFaq', 'Clicked');
              }}
              rel="noopener"
              blank
              alternate
              implicit
            >
              <BpkText textStyle={TEXT_STYLES.sm}>{strings.helpAnchorText}</BpkText>
            </BpkLink>
          </li>
          {showCultureSelectorButton && (
            <li id="culture-info">
              <div className={STYLES['SecondaryNav__culture-selector']}>
                {cultureSelectorButton}
              </div>
            </li>
          )}
          <li>
            <ErrorBoundary
              componentName="HeaderSavedButton"
              onError={onSavedButtonError}
              fallback={
                <SavedListButton ariaLabel={strings.savedFlights} onClick={onSavedListClick} />
              }
            >
              <SavedListButton ariaLabel={strings.savedFlights} onClick={onSavedListClick} />
            </ErrorBoundary>
          </li>
          {loginFormProps && (
            <LoginButton
              userLoggedIn={userLoggedIn}
              onLoginClick={(e, isLoggedIn) => loginClickHandler(e, isLoggedIn, 'header')}
              loginText={strings.login}
              accountText={strings.userAccount}
              getProfilePicture={getProfilePicture}
              loginButtonVariant={loginButtonVariant}
            />
          )}
        </ul>
      </nav>
      {}
      {loginFormProps ? (
        <LoginForm
          {...loginFormProps}
          entryPage={loginFormProps.entryPage}
          logger={logger}
          locale={locale}
          market={market}
          onClose={onLoginFormClose}
          trackEventFn={trackEventFn}
        />
      ) : null}
    </>
  );
};

export default SecondaryNav;
